import { ErrorMessage, Form, Formik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { FilePond } from "react-filepond";
import * as Yup from "yup";
import { upsertSalesContractVerifiedDocumentsApi } from "../../../../services/_sales";
import { getFileNameFromUrl } from "../../../../components/Helper";
import { successToast } from "../../../../components/Toast";

const verifiedDocumentInitialValues = {
  existing_files: [],
  new_files: [],
};

const verifiedDocumentValidationSchema = Yup.object().shape({
  existing_files: Yup.array().of(Yup.string()).notRequired(),
  new_files: Yup.array()
    .of(
      Yup.object().shape({
        fileName: Yup.string().required("File name is required"),
        fileData: Yup.string().required("File data is required"),
      })
    )
    .notRequired(),
});

interface AddVerifiedDocumentModalProps {
  verifiedDocumentStatus: boolean;
  verifiedDocumentData: {
    sales_contract_id: string;
    verified_documents: string[];
  };
  closeVerifiedDocumentModal: () => void;
  refetchSalesContractData: () => void;
}

const VerifiedDocumentModal: React.FC<AddVerifiedDocumentModalProps> = ({
  verifiedDocumentStatus,
  verifiedDocumentData,
  closeVerifiedDocumentModal,
  refetchSalesContractData,
}) => {
  const [initialValues, setInitialValues] = useState<any>({
    existing_files: [],
    new_files: [],
  });
  const [filePondFiles, setFilePondFiles] = useState([]);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);

  useEffect(() => {
    setInitialValues({
      existing_files: verifiedDocumentData.verified_documents || [],
      new_files: [],
    });
  }, [verifiedDocumentData]);

  const handleCloseModal = () => {
    setInitialValues(verifiedDocumentInitialValues);
    setFilePondFiles([]);
    closeVerifiedDocumentModal();
  };

  const handleFileChange = useCallback((fileItems: any, setFieldValue: any) => {
    const files = fileItems
      .map((fileItem: any) => {
        if (fileItem?.file) {
          return {
            fileName: fileItem.file.name,
            fileData: fileItem.getFileEncodeDataURL(), // Encodes to base64
          };
        }
        return null;
      })
      .filter(Boolean);
    setFieldValue("new_files", files);
  }, []);

  const handleDeleteFile = (
    values: any,
    delete_file: any,
    setFieldValue: any
  ) => {
    const updatedFiles = values.existing_files.filter(
      (file: any) => file !== delete_file
    );
    setFieldValue("existing_files", updatedFiles);
  };

  const handleSubmit = async (values: any) => {
    setSubmitLoader(true);
    const sendData = {
      verified_documents: {
        existing_files: values.existing_files,
        new_files: values.new_files || [],
      },
    };
    const result: any = await upsertSalesContractVerifiedDocumentsApi(
      verifiedDocumentData.sales_contract_id,
      sendData
    );
    if (result.isSuccess) {
      setSubmitLoader(false);
      refetchSalesContractData();
      handleCloseModal();
      successToast(result.message);
    } else {
      setSubmitLoader(false);
    }
  };

  return (
    <>
      {verifiedDocumentStatus && (
        <div
          className={`modal fade ${verifiedDocumentStatus ? "show" : ""}`}
          id="verifiedDocumentModal"
          aria-labelledby="verifiedDocumentModalLabel"
          aria-hidden={!verifiedDocumentStatus}
          tabIndex={-3}
          style={{ display: verifiedDocumentStatus ? "block" : "none" }}
        >
          <div className="modal-dialog modal-lg modal-simple modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body p-4">
                {/* Close modal button */}
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={handleCloseModal}
                />

                {/* Title */}
                <div className="text-center mb-4">
                  <h4 className="mb-2">Verified Documents</h4>
                </div>

                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={verifiedDocumentValidationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values, setFieldValue }) => {
                    return (
                      <Form className="dt_adv_search w-100">
                        {/* Upload document field */}
                        <div className="col-12">
                          <label htmlFor="new files" className="form-label">
                            <h6>Upload Verified Documents:</h6>
                          </label>
                          <div className="file-pond-main-block">
                            <FilePond
                              files={filePondFiles}
                              onupdatefiles={(fileItems: any) => {
                                setFilePondFiles(fileItems);
                                handleFileChange(fileItems, setFieldValue);
                              }}
                              allowMultiple
                              name="new_files"
                              labelIdle="Drag & drop files or click to browse"
                            />
                          </div>
                          <ErrorMessage
                            name="new_files"
                            component="div"
                            className="text-danger"
                          />
                        </div>

                        {/* Document List */}
                        <div className="col-12 mt-5 border">
                          <div
                            className="row px-3 py-2"
                            style={{ minHeight: "60px" }}
                          >
                            {values.existing_files?.length > 0 ? (
                              values.existing_files.map((file: any) => {
                                const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${file}`;
                                return (
                                  <>
                                    <div
                                      className="col-12 col-sm-4 my-2"
                                      style={{
                                        height: "100%",
                                        position: "relative",
                                      }}
                                    >
                                      <div className="form-check custom-option custom-option-icon">
                                        <label
                                          className="form-check-label custom-option-content"
                                          onClick={() =>
                                            window.open(imageUrl, "_blank")
                                          }
                                        >
                                          <span className="custom-option-body">
                                            <small>
                                              {getFileNameFromUrl(imageUrl)}
                                            </small>
                                          </span>
                                        </label>
                                      </div>
                                      <i
                                        className="fa-solid fa-xmark remove_document_file_btn"
                                        onClick={() =>
                                          handleDeleteFile(
                                            values,
                                            file,
                                            setFieldValue
                                          )
                                        }
                                      ></i>
                                    </div>
                                  </>
                                );
                              })
                            ) : (
                              <div className="col d-flex align-items-center justify-content-center">
                                No Documents
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Submit Close button */}
                        <div className="col-12 mt-5 d-flex justify-content-end">
                          <button
                            type="submit"
                            className="btn btn-primary mx-2"
                            disabled={submitLoader}
                          >
                            {submitLoader && (
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            )}
                            Submit
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary mx-2"
                            onClick={handleCloseModal}
                          >
                            Close
                          </button>
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VerifiedDocumentModal;
